import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, HeartPulse, AlertTriangle, Users, CalendarCheck, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export default function HealthPanel({ isAdmin = false }) {
  const [data, setData] = useState(null);
  const [recap, setRecap] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([
        axios.get(`${API}/health/clients`),
        axios.get(`${API}/health/recap/today`),
      ]);
      setData(c.data); setRecap(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de charger les fiches santé.');
      setData({ clients: [], total: 0, alerts: 0 });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (userId) => {
    setLoadingDetail(true); setDetail({});
    try {
      const r = await axios.get(`${API}/health/clients/${userId}`);
      setDetail(r.data);
    } catch (e) { toast.error('Fiche introuvable.'); setDetail(null); }
    finally { setLoadingDetail(false); }
  };

  const runRecap = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/health/recap/run`);
      toast.success(`Récapitulatifs envoyés : ${r.data.recaps_sent} coach(s).`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setRunning(false); }
  };

  if (!data) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C99A55]" /></div>;

  return (
    <div className="space-y-6" data-testid="health-panel">
      <div className="flex items-center gap-2">
        <HeartPulse className="w-5 h-5 text-[#C99A55]" strokeWidth={1.6} />
        <h2 className="font-heading text-xl text-[#493329]">Fiches santé & alertes</h2>
      </div>
      <p className="text-xs text-[#9A8B78] -mt-3">Lecture seule — remplies par les clientes. Confidentiel, à usage d'adaptation des séances.</p>

      {/* Récap du jour */}
      <Card className="bg-white border-[#E9DDCC] rounded-2xl">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CalendarCheck className="w-4 h-4 text-[#C99A55]" />
              <h3 className="font-medium text-[#493329] text-sm">Clientes attendues aujourd'hui{recap ? ` · ${recap.day}` : ''}</h3>
            </div>
            {isAdmin && (
              <Button onClick={runRecap} disabled={running} size="sm" variant="outline"
                className="rounded-full text-xs border-[#E9DDCC] text-[#7A6455]" data-testid="run-recap-btn">
                {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Envoyer le récap
              </Button>
            )}
          </div>
          {recap && recap.courses.length > 0 ? (
            <div className="space-y-3" data-testid="recap-courses">
              {recap.courses.map((co) => (
                <div key={co.slot_id} className="border border-[#F0E8DA] rounded-xl p-3">
                  <p className="text-xs font-medium text-[#493329]">{co.time} · {co.type}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {co.participants.map((p, i) => (
                      <span key={i} className={`text-xs px-2.5 py-1 rounded-full ${p.has_alert ? 'bg-[#F7E6E1] text-[#8A4A3B]' : 'bg-[#EDF2EA] text-[#5C7057]'}`}>
                        {p.name}{p.has_alert ? ' ⚠️' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-[#9A8B78]" data-testid="recap-empty">Aucune cliente attendue aujourd'hui{isAdmin ? '' : ' sur vos créneaux'}.</p>
          )}
        </CardContent>
      </Card>

      {/* Stats + liste fiches */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="bg-white border-[#E9DDCC] rounded-2xl"><CardContent className="p-4 flex items-center gap-3">
          <Users className="w-4 h-4 text-[#C99A55]" /><div><p className="text-[10px] uppercase tracking-wider text-[#7A6455]">Fiches santé</p><p className="text-2xl font-semibold text-[#493329] leading-none" data-testid="health-total">{data.total}</p></div>
        </CardContent></Card>
        <Card className="bg-white border-[#E9DDCC] rounded-2xl"><CardContent className="p-4 flex items-center gap-3">
          <AlertTriangle className="w-4 h-4 text-[#C76542]" /><div><p className="text-[10px] uppercase tracking-wider text-[#7A6455]">Points de vigilance</p><p className="text-2xl font-semibold text-[#493329] leading-none" data-testid="health-alerts">{data.alerts}</p></div>
        </CardContent></Card>
      </div>

      <div className="space-y-2" data-testid="health-clients-list">
        {data.clients.length === 0 && <p className="text-sm text-[#9A8B78] text-center py-6">Aucune fiche santé renseignée pour le moment.</p>}
        {data.clients.map((c) => (
          <button key={c.user_id} onClick={() => openDetail(c.user_id)}
            className="w-full text-left bg-white border border-[#E9DDCC] rounded-xl p-3.5 hover:border-[#C99A55] transition-colors flex items-center justify-between"
            data-testid={`health-client-${c.user_id}`}>
            <div>
              <p className="text-sm font-medium text-[#493329]">{c.name}</p>
              <p className="text-xs text-[#9A8B78]">{c.email} · maj {fmtDate(c.updated_at)}</p>
            </div>
            {c.has_alert
              ? <span className="text-xs px-2.5 py-1 rounded-full bg-[#F7E6E1] text-[#8A4A3B] whitespace-nowrap">⚠️ {c.alert_summary}</span>
              : <span className="text-xs px-2.5 py-1 rounded-full bg-[#EDF2EA] text-[#5C7057]">OK</span>}
          </button>
        ))}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#FBF8F2] border-[#E9DDCC] max-w-lg" data-testid="health-detail-dialog">
          <DialogHeader><DialogTitle className="font-heading text-[#493329]">{detail?.name || 'Fiche santé'}</DialogTitle></DialogHeader>
          {loadingDetail ? <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" /></div> : detail && (
            <div className="space-y-4 text-sm">
              <p className="text-xs text-[#9A8B78]">{detail.email} · {detail.phone || 'sans téléphone'}</p>
              {detail.has_alert && <div className="bg-[#F7E6E1] border border-[#E4B9AC] rounded-xl p-2.5 text-xs text-[#8A4A3B]">⚠️ {detail.alert_summary}</div>}
              <Field label="Blessures, contre-indications & zones sensibles" value={detail.contraindications} />
              <Field label="Grossesse / post-partum" value={detail.pregnancy ? (detail.pregnancy_detail || 'Oui') : 'Non'} />
              <Field label="Antécédents médicaux" value={detail.medical_history} />
              <p className="text-[11px] text-[#a08f7e] pt-1">Information confidentielle — usage strictement professionnel.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-[#9A8B78] mb-1">{label}</p>
    <p className="text-[#493329] whitespace-pre-wrap">{(value && String(value).trim()) || <span className="text-[#C3B8A6]">Non renseigné</span>}</p>
  </div>
);
